import { DEFAULT_PRICE_TABLE, BRA_SIZE_COLS, CLOTHES_SIZE_COLS, BRA_SIZE_COL_TERRITORIES, CLOTHES_SIZE_COL_TERRITORIES, getTableCols } from '../config/constants';
import { uid, convertPrice, TWO_DIM_VARIANT_TYPE, deriveTitleNoColour, syncImagesFields, exportMediaUrls } from './products';
import { makeProduct } from './products';
import { BUILTIN_FIELDS } from '../config/fields';
import { filenameFromUrl, isOwnStorageUrl } from './imageLinks';

function escCSV(val) {
  if (val === null || val === undefined) return '';
  let s = String(val);
  // Formula/CSV injection: a cell starting with =, +, -, @, tab, or CR is interpreted as a
  // formula by Excel/Sheets when the file is opened — any team member (not just managers)
  // can fill in fields like Title/Tags/Vendor that end up here. Prefix a single quote to
  // force text interpretation (the standard OWASP CSV-injection mitigation).
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

// A metafield's "Shopify key" may be a bare key ("materials") or a full "namespace.key"
// override ("specifications.size") when it lives outside the tenant's default namespace.
function splitNsKey(raw, defaultNs) {
  const s = (raw || '').trim();
  const dot = s.indexOf('.');
  return dot === -1 ? { ns: defaultNs, key: s } : { ns: s.slice(0, dot), key: s.slice(dot + 1) };
}
function resolveMetaNamespaceKey(field, defaultNs) {
  return splitNsKey(field.shopifyKey || field.key, defaultNs);
}
// Metafield column header from a possibly-namespaced key ("color" → default ns; "specs.color" → specs).
function metaHeader(nsKey, defaultNs, type) {
  const { ns, key } = splitNsKey(nsKey, defaultNs);
  return `Metafield: ${ns}.${key} [${type}]`;
}

// A custom field's CSV column header depends on its kind: a 'standard' field maps to a native
// Shopify/Matrixify column and exports using the tenant-typed header verbatim (no metafield
// wrapper); a 'metafield' field (default, for backward compatibility with fields saved before
// `kind` existed) exports as a namespaced Metafield column, same as always.
function fieldColumnHeader(field, defaultNs) {
  if (field.kind === 'standard') return (field.columnHeader || field.label || field.key).trim();
  const r = resolveMetaNamespaceKey(field, defaultNs);
  return `Metafield: ${r.ns}.${r.key} [${field.type}]`;
}

// Default bare Shopify metafield key for each built-in field that exports as a metafield —
// derived from the field definitions (config/fields), so those are the single source of truth.
// Tenants override any of these via the field's Shopify key in Settings → Fields.
export const FIELD_MAPPING_DEFAULTS = Object.fromEntries(
  BUILTIN_FIELDS.filter(f => f.export?.kind === 'metafield').map(f => [f.key, f.export.key])
);

function fieldMetaKey(tenantConfig, field) {
  return tenantConfig?.fieldMappings?.[field] || FIELD_MAPPING_DEFAULTS[field];
}

// Structural anchors can never be deleted out of the export, even if a hand-crafted config lists
// them in disabledFields — bespoke machinery (identity, price, image, variant sku/size) needs them.
const LOCKED_FIELD_KEYS = new Set(BUILTIN_FIELDS.filter(f => f.locked).map(f => f.key));
const isRemoved = (removed, key) => key && !LOCKED_FIELD_KEYS.has(key) && removed.includes(key);

export function buildCSV(products, terr, priceTable = DEFAULT_PRICE_TABLE, sizeTable = null, tenantConfig = null) {
  const ns = tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store';
  const removed = tenantConfig?.disabledFields || [];   // a deleted built-in field is gone entirely — no column
  const metaFields = tenantConfig?.metafields || [];
  // Product-level custom fields write their value once (first variant row); variant-scope custom
  // fields (native columns) write per variant row from the variant object.
  const productCustom = metaFields.filter(f => f.scope !== 'variant');
  const variantCustom = metaFields.filter(f => f.scope === 'variant');
  const colorKey  = fieldMetaKey(tenantConfig, 'metaColor');
  const compKey   = fieldMetaKey(tenantConfig, 'composition');
  const fitKey    = fieldMetaKey(tenantConfig, 'fitFabric');
  const careKey   = fieldMetaKey(tenantConfig, 'careInstructions');
  const weightKey = fieldMetaKey(tenantConfig, 'fabricWeight');

  // Ordered Shopify/Matrixify columns. Scaffolding columns (Option/Inventory/price/etc.) are
  // always present; a field-backed column carries its built-in field `key` and its EXACT Shopify
  // header (native) or namespaced Metafield header, so a deleted (removed) built-in drops out of
  // the export entirely — treated exactly like a custom field that was never added. Custom fields
  // come straight from config.metafields.
  const cols = [
    { h: 'Handle',                          key: 'handle',          get: c => c.p.handle },
    { h: 'Title',                           key: 'title',           get: c => c.isFirst ? c.terrTitle : '' },
    { h: 'Vendor',                          key: 'vendor',          get: c => c.isFirst ? c.p.vendor : '' },
    { h: 'Product Category',                key: 'productCategory', get: c => c.isFirst ? (c.p.productCategory || '') : '' },
    { h: 'Type',                            key: 'type',            get: c => c.isFirst ? c.p.type : '' },
    { h: 'Tags',                            key: 'tags',            get: c => c.isFirst ? (c.p.tags || []).join(', ') : '' },
    { h: 'Published',                                               get: c => c.isFirst ? c.p.published : '' },
    { h: 'Option1 Name',                                            get: c => c.v.opt1Name || 'Title' },
    { h: 'Option1 Value',                                           get: c => c.tOpt1Val || 'Default Title' },
    { h: 'Option2 Name',                                            get: c => c.v.opt2Name || '' },
    { h: 'Option2 Value',                                           get: c => c.tOpt2Val || '' },
    { h: 'Variant SKU',                     key: 'sku',             get: c => c.v.sku },
    { h: 'Variant Grams',                   key: 'grams',           get: c => c.v.grams || 200 },
    { h: 'Variant Inventory Tracker',                              get: () => 'shopify' },
    { h: 'Variant Inventory Policy',                               get: () => 'deny' },
    { h: 'Variant Fulfillment Service',                           get: () => 'manual' },
    { h: 'Variant Price',                                          get: c => c.varPrice },
    { h: 'Variant Compare At Price',        key: 'compareAtPrice',  get: c => c.p.compareAtPrice || '' },
    { h: 'Variant Requires Shipping',                             get: () => 'TRUE' },
    { h: 'Variant Taxable',                                       get: () => 'TRUE' },
    { h: 'Variant Barcode',                 key: 'barcode',         get: c => c.v.barcode || '' },
    { h: 'Gift Card',                                             get: c => c.isFirst ? 'FALSE' : '' },
    { h: 'Variant Weight Unit',                                  get: () => 'kg' },
    { h: 'Cost per item',                   key: 'costPerItem',     get: c => c.v.costPerItem || '' },
    { h: 'Variant HS Code',                 key: 'hsCode',          get: c => c.v.hsCode || c.p.hsCode || '' },
    { h: 'Variant Country of Origin',       key: 'coo',             get: c => c.v.coo || c.p.coo || '' },
    { h: 'Status',                          key: 'shopifyStatus',   get: c => c.isFirst ? c.p.shopifyStatus : '' },
    { h: 'Body HTML',                       key: 'bodyHtml',        get: c => c.isFirst ? c.terrBodyHtml : '' },
    { h: metaHeader(colorKey, ns, 'single_line_text_field'), key: 'metaColor', get: c => c.isFirst ? c.p.metaColor : '' },
    { h: `Metafield: ${ns}.title [single_line_text_field]`,       get: c => c.isFirst ? (c.p.titleNoColour || deriveTitleNoColour(c.p.title)) : '' },
    ...productCustom.map(f => ({ h: fieldColumnHeader(f, ns), get: c => c.isFirst ? (c.p[f.key] || '') : '' })),
    { h: `Metafield: ${ns}.badges [list.single_line_text_field]`, get: c => c.isFirst ? (c.p.metaBadges || []).join('|') : '' },
    { h: metaHeader(fitKey, ns, 'multi_line_text_field'),    key: 'fitFabric',        get: c => c.isFirst ? c.p.fitFabric : '' },
    { h: metaHeader(careKey, ns, 'multi_line_text_field'),   key: 'careInstructions', get: c => c.isFirst ? c.p.careInstructions : '' },
    { h: metaHeader(weightKey, ns, 'single_line_text_field'), key: 'fabricWeight',    get: c => c.isFirst ? (c.p.fabricWeight || '') : '' },
    { h: metaHeader(compKey, ns, 'single_line_text_field'),  key: 'composition',      get: c => c.isFirst ? (c.p.composition || '') : '' },
    ...variantCustom.map(f => ({ h: fieldColumnHeader(f, ns), get: c => c.v[f.key] ?? '' })),
  ].filter(col => !isRemoved(removed, col.key));

  const rows = [cols.map(c => c.h).map(escCSV).join(',')];

  const tableCols = getTableCols(tenantConfig?.territories);

  for (const p of products) {
    const terrPriceManual = p.territoryPrices?.[terr.code];
    const terrPriceAuto   = convertPrice(p.basePrice, terr.col, priceTable, tableCols);
    const terrPrice       = parseFloat(terrPriceManual || terrPriceAuto) || 0;
    const trans           = p.translations?.[terr.code];
    const terrTitle       = trans?.title    || p.title;
    const terrBodyHtml    = trans?.bodyHtml || p.bodyHtml;

    const variants = p.variants.length > 0 ? p.variants : [{
      id: '_', sku: p.skuBase, opt1Name: 'Title', opt1Val: 'Default Title',
      opt2Name: '', opt2Val: '', price: p.basePrice || '0', barcode: '', grams: 200,
    }];

    const firstVarGbp = parseFloat(variants[0]?.price) || parseFloat(p.basePrice) || 0;

    variants.forEach((v, i) => {
      const isFirst = i === 0;
      const varGbp  = parseFloat(v.price) || 0;
      let varPrice;
      if (!terr.col) {
        varPrice = varGbp.toFixed(2);
      } else if (terrPriceManual && isFirst) {
        varPrice = parseFloat(terrPriceManual).toFixed(2);
      } else {
        const ratio = firstVarGbp > 0 ? varGbp / firstVarGbp : 1;
        varPrice = (terrPrice * ratio).toFixed(2);
      }

      const { opt1Val: tOpt1Val, opt2Val: tOpt2Val } = translateVariantSize(
        v.opt1Val || '', v.opt2Name || '', v.opt2Val || '', terr, sizeTable, tenantConfig?.sizeCategories
      );

      const ctx = { p, v, isFirst, terrTitle, terrBodyHtml, varPrice, tOpt1Val, tOpt2Val };
      rows.push(cols.map(c => c.get(ctx)).map(escCSV).join(','));
    });
  }

  return rows.join('\n');
}

export function buildMetafieldsCSV(products, tenantConfig = null) {
  const ns = tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store';
  // 'standard' fields map to a native column already present in the main product CSV —
  // they don't belong in this supplementary metafields-only sheet.
  const removed  = tenantConfig?.disabledFields || [];
  const metaFields = (tenantConfig?.metafields || []).filter(f => f.kind !== 'standard');
  const colorKey = fieldMetaKey(tenantConfig, 'metaColor');
  const showColor = !isRemoved(removed, 'metaColor');   // a deleted built-in has no column here either
  const headers = [
    'Handle', 'SKU', 'Title', 'Title (no colour)', 'Body HTML',
    ...(showColor ? [metaHeader(colorKey, ns, 'single_line_text_field')] : []),
    `Metafield: ${ns}.title [single_line_text_field]`,
    ...metaFields.map(f => { const r = resolveMetaNamespaceKey(f, ns); return `Metafield: ${r.ns}.${r.key} [${f.type}]`; }),
    `Metafield: ${ns}.badges [list.single_line_text_field]`,
  ];
  const rows = [headers.map(escCSV).join(',')];
  for (const p of products) {
    const titleNoColour = p.titleNoColour || deriveTitleNoColour(p.title);
    const row = [
      p.handle, p.skuBase, p.title, titleNoColour, p.bodyHtml,
      ...(showColor ? [p.metaColor] : []), titleNoColour,
      ...metaFields.map(f => p[f.key] || ''),
      (p.metaBadges || []).join('|'),
    ];
    rows.push(row.map(escCSV).join(','));
  }
  return rows.join('\n');
}

export function buildFitFabricCSV(products, tenantConfig = null) {
  const ns = tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store';
  const removed   = tenantConfig?.disabledFields || [];
  const fitKey    = fieldMetaKey(tenantConfig, 'fitFabric');
  const weightKey = fieldMetaKey(tenantConfig, 'fabricWeight');
  const showFit    = !isRemoved(removed, 'fitFabric');
  const showWeight = !isRemoved(removed, 'fabricWeight');
  const headers = ['Handle',
    ...(showFit    ? [metaHeader(fitKey, ns, 'multi_line_text_field')] : []),
    ...(showWeight ? [metaHeader(weightKey, ns, 'single_line_text_field')] : []),
  ];
  const rows = [headers.map(escCSV).join(',')];
  for (const p of products) rows.push([p.handle,
    ...(showFit    ? [p.fitFabric] : []),
    ...(showWeight ? [p.fabricWeight || ''] : []),
  ].map(escCSV).join(','));
  return rows.join('\n');
}

export function buildCareInstructionsCSV(products, tenantConfig = null) {
  const ns = tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store';
  const removed = tenantConfig?.disabledFields || [];
  const careKey = fieldMetaKey(tenantConfig, 'careInstructions');
  if (isRemoved(removed, 'careInstructions')) return ['Handle', ...products.map(p => p.handle)].map(escCSV).join('\n');
  const headers = ['Handle', metaHeader(careKey, ns, 'multi_line_text_field')];
  const rows = [headers.map(escCSV).join(',')];
  for (const p of products) rows.push([p.handle, p.careInstructions].map(escCSV).join(','));
  return rows.join('\n');
}

const DEFAULT_BRA_COLS     = BRA_SIZE_COLS.map((label, i) => ({ label, territories: BRA_SIZE_COL_TERRITORIES[i] }));
const DEFAULT_CLOTHES_COLS = CLOTHES_SIZE_COLS.map((label, i) => ({ label, territories: CLOTHES_SIZE_COL_TERRITORIES[i] }));

// Map a territory code → column index using a category's own territory-per-column assignments
// (tenant-configured in Settings → Size Categories), so export works for any custom column layout.
function sizeColIdx(cols, terrCode) {
  const idx = (cols || []).findIndex(c => (c.territories || []).includes(terrCode));
  return idx === -1 ? null : idx;
}

// Translate opt1/opt2 values for a variant into territory-appropriate sizes.
// Size data (sizeTable) is stored keyed by each category's own id — see SizeTableView.jsx,
// which already reads/writes it generically for any tenant-defined category, not just 'bra'/
// 'clothes'. Only the lookup here used to hardcode those two ids; that's fixed below so a
// tenant's own custom category (e.g. "Footwear") translates correctly too.
export function translateVariantSize(opt1Val, opt2Name, opt2Val, terr, sizeTable, sizeCategories = null) {
  if (!sizeTable || !terr) return { opt1Val, opt2Val };

  const categories = sizeCategories?.length
    ? sizeCategories
    : [{ id: 'bra', cols: DEFAULT_BRA_COLS }, { id: 'clothes', cols: DEFAULT_CLOTHES_COLS }];

  // Two-dimension (Band/Cup) variants: opt2Name === 'Cup Size' identifies these regardless of
  // tenant — only ever set via the (opt-in) bra-size detection or a tenant's own two-dim preset.
  // The conventional 'bra' category id holds the band+cup lookup table for whichever tenant uses it.
  if (opt2Name === 'Cup Size' && opt2Val) {
    const braCat  = categories.find(c => c.id === 'bra');
    const braCols = braCat?.cols || DEFAULT_BRA_COLS;
    const col = sizeColIdx(braCols, terr.code);
    if (col !== null) {
      const ukKey = `${opt1Val}${opt2Val}`; // e.g. "34B"
      const terrSize = sizeTable[braCat?.id || 'bra']?.[ukKey]?.[col]; // e.g. "12B" or "75DD"
      if (terrSize) {
        const m = terrSize.match(/^(\d+)(DD|[A-G])$/);
        if (m) return { opt1Val: m[1], opt2Val: m[2] };
      }
    }
    return { opt1Val, opt2Val };
  }

  // Single-dimension variants: try every configured category (not just one hardcoded 'clothes'
  // id), so any tenant-defined category translates, not only ones named exactly 'clothes'.
  for (const cat of categories) {
    if (cat.id === 'bra') continue;
    const col = sizeColIdx(cat.cols || [], terr.code);
    if (col === null) continue;
    const terrSize = sizeTable[cat.id]?.[opt1Val]?.[col];
    if (terrSize) return { opt1Val: terrSize, opt2Val };
  }

  return { opt1Val, opt2Val };
}

export function buildWarehouseCSV(products, terr = null, priceTable = DEFAULT_PRICE_TABLE, sizeTable = null, opts = {}, tenantConfig = null) {
  const { includeImages = false, includeBody = false } = opts;
  const headers = [
    'Handle', 'Title', 'Vendor', 'Type', 'Published',
    'Option1 Name', 'Option1 Value', 'Option2 Name', 'Option2 Value',
    'Variant SKU', 'Variant Barcode', 'Variant Grams',
    'Variant Inventory Tracker', 'Variant Inventory Policy',
    'Variant Fulfillment Service', 'Variant Price',
    'Variant Requires Shipping', 'Variant Taxable', 'Variant Weight Unit',
    'Variant HS Code', 'Variant Country of Origin', 'Cost per item',
    'Status',
    ...(includeBody   ? ['Body HTML'] : []),
    ...(includeImages ? ['Image Src', 'Image Position'] : []),
  ];
  // Same formula-injection guard as escCSV above — quoting alone doesn't stop Excel/Sheets
  // treating a leading =, +, -, @ as a formula, so it still needs the leading-quote prefix.
  const esc = v => {
    let s = String(v ?? '');
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return `"${s.replace(/"/g, '""')}"`;
  };
  const rows = [headers.join(',')];
  const tableCols = getTableCols(tenantConfig?.territories);

  for (const p of products) {
    const variants = p.variants?.filter(v => v.sku) ?? [];
    if (!p.handle || !p.title || variants.length === 0) continue;

    let firstVarGbp = 0, terrBasePrice = 0;
    if (terr) {
      const manualNum = parseFloat(p.territoryPrices?.[terr.code]) || 0;
      const autoNum   = parseFloat(convertPrice(p.basePrice, terr.col, priceTable, tableCols)) || 0;
      terrBasePrice   = manualNum || autoNum;
      firstVarGbp     = parseFloat(variants[0]?.price) || parseFloat(p.basePrice) || 0;
    }

    variants.forEach((v, i) => {
      const isFirst = i === 0;
      let varPrice;
      if (!terr) {
        varPrice = v.price || '0';
      } else {
        const varGbp    = parseFloat(v.price) || 0;
        const manualNum = parseFloat(p.territoryPrices?.[terr.code]) || 0;
        if (!terr.col) {
          // GBP territory (UK): use variant's own GBP price if set, else product base price
          const ukBase = manualNum || parseFloat(p.basePrice) || 0;
          varPrice = (varGbp > 0 ? varGbp : ukBase).toFixed(2);
        } else if (manualNum > 0 && isFirst) {
          // Non-GBP with manual override on first variant
          varPrice = manualNum.toFixed(2);
        } else {
          // Auto-calculated: apply ratio only when both first and current variant have GBP prices
          const ratio = (firstVarGbp > 0 && varGbp > 0) ? varGbp / firstVarGbp : 1;
          varPrice = (terrBasePrice * ratio).toFixed(2);
        }
      }

      const { opt1Val: tOpt1Val, opt2Val: tOpt2Val } = translateVariantSize(
        v.opt1Val || '', v.opt2Name || '', v.opt2Val || '', terr, sizeTable, tenantConfig?.sizeCategories
      );

      rows.push([
        esc(p.handle),
        esc(isFirst ? p.title : ''),
        esc(isFirst ? (p.vendor || '') : ''),
        esc(isFirst ? (p.type || '') : ''),
        esc('FALSE'),
        esc(v.opt1Name || 'Size'),
        esc(tOpt1Val),
        esc(v.opt2Name || ''),
        esc(tOpt2Val),
        esc(v.sku),
        esc(v.barcode || ''),
        esc(v.grams || 200),
        esc('shopify'),
        esc('deny'),
        esc('manual'),
        esc(varPrice),
        esc('TRUE'),
        esc('TRUE'),
        esc('g'),
        esc(v.hsCode || ''),
        esc(v.coo || ''),
        esc(v.costPerItem || ''),
        esc('draft'),
        ...(includeBody   ? [esc(isFirst ? (p.bodyHtml || '') : '')] : []),
        ...(includeImages ? [esc(isFirst ? (p.imageUrl || '') : ''), esc(isFirst ? '1' : '')] : []),
      ].join(','));
    });

    // Additional media rows (Shopify requires one row per image). The primary is already on
    // the first variant row; videos are included because Matrixify imports them from Image Src.
    if (includeImages) {
      const rest = exportMediaUrls(p).filter(u => u !== p.imageUrl);
      const firstPos = p.imageUrl ? 2 : 1;
      const emptyVariantCols = Array(headers.length - 3).fill(esc(''));
      rest.forEach((url, idx) => {
        rows.push([esc(p.handle), ...emptyVariantCols, esc(url), esc(idx + firstPos)].join(','));
      });
    }
  }
  return rows.join('\n');
}

export function warehouseTerritories(products, allTerr) {
  // UK always included; other territories included if any product has a manual price entered
  return allTerr.filter(t => t.code === 'UK' || t.code === 'STG'
    ? t.code === 'UK'
    : products.some(p => p.territoryPrices?.[t.code]));
}

export function downloadCSV(content, filename) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Which fields block export when missing is tenant-configurable (Settings → Fields, the Required
// toggle) — not every tenant tracks Fit & Fabric or Care Instructions. Default matches the field
// definitions' requiredDefault set so existing tenants see no change until they opt out.
const REQUIRED_FIELD_DEFS = {
  skuBase:          { check: p => !!p.skuBase,                                   message: 'Missing base SKU' },
  title:            { check: p => !!p.title,                                     message: 'Missing title' },
  bodyHtml:         { check: p => !!p.bodyHtml,                                  message: 'Missing description' },
  basePrice:        { check: p => !!p.basePrice && !!parseFloat(p.basePrice),    message: 'Price is £0 or missing' },
  fitFabric:        { check: p => !!p.fitFabric,                                 message: 'Missing fit & fabric' },
  careInstructions: { check: p => !!p.careInstructions,                          message: 'Missing care instructions' },
  imageUrl:         { check: p => !!p.imageUrl,                                  message: 'Missing primary image' },
};

// Default required-for-export set — derived from the field definitions' requiredDefault flag
// (config/fields) rather than duplicated here. REQUIRED_FIELD_DEFS above supplies each one's
// bespoke check + error message.
export const DEFAULT_REQUIRED_EXPORT_FIELDS = BUILTIN_FIELDS.filter(f => f.requiredDefault).map(f => f.key);

export function validateProducts(products, requiredFields = DEFAULT_REQUIRED_EXPORT_FIELDS) {
  const errors = [], warnings = [], excludedIds = new Set();
  for (const p of products) {
    const err = [], warn = [];
    for (const key of requiredFields) {
      const def = REQUIRED_FIELD_DEFS[key];
      if (def && !def.check(p)) err.push(def.message);
    }
    if (p.variants.length > 0 && !p.variants.some(v => v.barcode)) warn.push('No barcodes set');
    if (p.bodyHtml && !p.bodyHtml.includes('<'))  warn.push('Description does not appear to be HTML');
    if (err.length)  { errors.push({ product: p.title || p.skuBase || p.id, issues: err }); excludedIds.add(p.id); }
    if (warn.length) warnings.push({ product: p.title || p.skuBase || p.id, issues: warn });
  }
  return { errors, warnings, excludedIds };
}

function parseCSVCells(text) {
  const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let row = [], current = '', inQ = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '"') {
      if (inQ && src[i + 1] === '"') { current += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      row.push(current); current = '';
    } else if (ch === '\n' && !inQ) {
      row.push(current); current = '';
      rows.push(row); row = [];
    } else { current += ch; }
  }
  row.push(current);
  rows.push(row);
  return rows;
}

export function parseCSV(text) {
  const rawRows = parseCSVCells(text).filter(r => r.some(c => c !== ''));
  if (rawRows.length === 0) return [];
  const headers = rawRows[0].map(h => h.trim().toLowerCase());
  return rawRows.slice(1).map(vals => {
    const obj = {};
    headers.forEach((h, j) => { obj[h] = (vals[j] || '').trim(); });
    return obj;
  });
}

// Strips everything but letters/digits so "HS Code", "hs_code" and "HS-Code" all normalize
// the same way — tenants export from wildly different systems with inconsistent header casing,
// spacing and punctuation, and this app's own re-import must also match its own export headers.
const normHeader = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export function findCol(row, ...candidates) {
  for (const c of candidates) {
    const nc  = normHeader(c);
    const key = Object.keys(row).find(k => normHeader(k) === nc);
    if (key !== undefined) return row[key];
  }
  return '';
}

// Strip any currency symbol / thousands separator from an imported price so it stores as a bare
// number: "£45.00", a mis-encoded "Â£45.00", or "1,234.50" all become "45.00" / "1234.50". The
// UI prepends the territory symbol itself, so a stored "£" would render as "££45.00".
function cleanPrice(s) {
  return String(s ?? '').replace(/[^0-9.]/g, '');
}

export function importCSVs(csvTexts, existingProducts = [], tenantConfig = null) {
  const ns = tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store';
  const metaFields = tenantConfig?.metafields || [];
  const map = {};
  const touched = new Set();
  // key -> [{ url, position }]. Images are gathered across every row of a product before
  // being applied, because Shopify sheets spread one image per row under a repeated handle.
  const imageRows = {};

  existingProducts.forEach(p => {
    const key = p.handle || p.skuBase;
    if (key) map[key] = { ...p, variants: [...(p.variants || [])] };
  });

  const skuToKey = {};
  const titleToKey = {};
  existingProducts.forEach(p => {
    const key = p.handle || p.skuBase;
    if (!key) return;
    if (p.skuBase) skuToKey[p.skuBase.toUpperCase()] = key;
    (p.variants || []).forEach(v => { if (v.sku) skuToKey[v.sku.toUpperCase()] = key; });
    if (p.title) titleToKey[p.title.toLowerCase().trim()] = key;
  });

  for (const text of csvTexts) {
    for (const row of parseCSV(text)) {
      let key = findCol(row, 'handle');

      const csvBaseSku    = findCol(row, 'base sku', 'sku base');
      const csvVariantSku = findCol(row, 'variant sku');
      const csvGenericSku = findCol(row, 'sku');
      const csvLookupSku  = csvBaseSku || csvGenericSku || csvVariantSku;

      if (!key || !map[key]) {
        if (csvLookupSku) {
          const skuUp = csvLookupSku.toUpperCase();
          if (skuToKey[skuUp]) {
            key = skuToKey[skuUp];
          } else {
            const baseGuess = skuUp.replace(/-[^-]+$/, '');
            if (baseGuess && skuToKey[baseGuess]) key = skuToKey[baseGuess];
          }
          if (!key || !map[key]) key = key || csvLookupSku;
        }
      }

      if (!key || !map[key]) {
        const csvTitle = findCol(row, 'title', 'product title', 'product name');
        if (csvTitle && titleToKey[csvTitle.toLowerCase().trim()]) {
          key = titleToKey[csvTitle.toLowerCase().trim()];
        }
      }

      // Last resort: generate a handle-slug from title and create a new product.
      // Covers title-only CSVs where no existing products have handle/skuBase yet.
      if (!key) {
        const csvTitle = findCol(row, 'title', 'product title', 'product name');
        if (csvTitle) {
          key = csvTitle.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          titleToKey[csvTitle.toLowerCase().trim()] = key;
        }
      }

      if (!key) continue;
      if (!map[key]) map[key] = makeProduct();
      touched.add(key);
      const p = map[key];

      const csvHandle = findCol(row, 'handle');
      if (csvHandle && !p.handle) p.handle = csvHandle;
      if (!p.handle && key) p.handle = key; // persist auto-generated handle so future imports match
      const SIZE_SUFFIX = /-(\d{1,2}P?|XSS?L?|S\+?|SP|ML?|XL(?:XXL)?|XXL|XXXL|\d{2}(?:DD|[A-F]))$/i;
      if (csvBaseSku && !p.skuBase) {
        p.skuBase = csvBaseSku;
      } else if (!csvBaseSku && csvVariantSku && !p.skuBase) {
        p.skuBase = csvVariantSku.replace(SIZE_SUFFIX, '');
      } else if (!csvBaseSku && csvGenericSku && !p.skuBase) {
        p.skuBase = csvGenericSku.replace(SIZE_SUFFIX, '');
      }
      if (p.skuBase) {
        skuToKey[p.skuBase.toUpperCase()] = key;
      }

      // Image columns. Shopify's export puts one image per row ("Image Src" + a 1-based
      // "Image Position") under a repeated handle; hand-made sheets more often pack the lot
      // into a single comma-separated cell. Both are collected as plain CDN links here and
      // re-hosted after parsing — importCSVs stays synchronous and network-free.
      const imgSrc   = findCol(row, 'image src', 'image url', 'image');
      const imgPos   = parseInt(findCol(row, 'image position'), 10);
      const bulkImgs = findCol(row, 'additional images', 'images');
      if (imgSrc || bulkImgs) {
        const bucket = imageRows[key] || (imageRows[key] = []);
        // Positions beyond the numbered ones start at 1000 so explicitly-positioned images
        // always sort first, while an un-numbered list keeps the order it was written in.
        const push = (raw, position) => {
          const url = String(raw || '').trim();
          if (!/^https?:\/\//i.test(url)) return;
          if (bucket.some(b => b.url === url)) return;
          bucket.push({ url, position: Number.isFinite(position) ? position : 1000 + bucket.length });
        };
        if (imgSrc) push(imgSrc, imgPos);
        if (bulkImgs) bulkImgs.split(',').forEach(u => push(u, NaN));
      }

      const title = findCol(row, 'title', 'product title', 'product name');
      if (title) p.title = title;
      p.titleNoColour      = findCol(row, `${ns}.title`, 'title (no colour)') || p.titleNoColour;
      p.bodyHtml           = findCol(row, 'body html', 'body_html', 'description')      || p.bodyHtml;
      p.vendor             = findCol(row, 'vendor')                                     || p.vendor;
      p.type               = findCol(row, 'type', 'product type')                       || p.type;
      p.productCategory    = findCol(row, 'product category', 'category')               || p.productCategory;
      p.shopifyStatus      = findCol(row, 'status')                                     || p.shopifyStatus;
      p.published          = findCol(row, 'published')                                  || p.published;
      const tagsStr        = findCol(row, 'tags');
      if (tagsStr) p.tags  = tagsStr.split(',').map(s => s.trim()).filter(Boolean);

      const colorKey  = fieldMetaKey(tenantConfig, 'metaColor');
      const compKey   = fieldMetaKey(tenantConfig, 'composition');
      const fitKey    = fieldMetaKey(tenantConfig, 'fitFabric');
      const careKey   = fieldMetaKey(tenantConfig, 'careInstructions');
      const weightKey = fieldMetaKey(tenantConfig, 'fabricWeight');

      p.metaColor = findCol(row, `${ns}.${colorKey}`, colorKey, 'color', 'colour') || p.metaColor;
      metaFields.forEach(f => {
        if (f.scope === 'variant') return; // per-variant custom fields aren't read at product level
        if (f.kind === 'standard') {
          p[f.key] = findCol(row, f.columnHeader || f.label || f.key) || p[f.key];
          return;
        }
        const r = resolveMetaNamespaceKey(f, ns);
        p[f.key] = findCol(row, `${r.ns}.${r.key}`) || p[f.key];
      });

      p.fitFabric        = findCol(row, `${ns}.${fitKey}`, fitKey, 'fit fabric')                 || p.fitFabric;
      p.careInstructions = findCol(row, `${ns}.${careKey}`, careKey, 'care instructions')        || p.careInstructions;
      p.composition       = findCol(row, `${ns}.${compKey}`, compKey, 'composition', 'fabric composition') || p.composition;
      p.fabricWeight       = findCol(row, `${ns}.${weightKey}`, weightKey, 'fabric weight', 'gsm')          || p.fabricWeight;

      // Product-level defaults for fields that can also appear per-variant below (HS Code,
      // COO, price). A single-row-per-product sheet only ever gives one value for these, so
      // capture it here regardless of whether a variant block runs for this row.
      const rowPrice  = cleanPrice(findCol(row, 'price', 'variant price', 'base price', 'retail price'));
      const rowHsCode = findCol(row, 'hs code', 'tariff code', 'commodity code');
      const rowCoo    = findCol(row, 'coo', 'country of origin');
      if (rowPrice  && !p.basePrice) p.basePrice = rowPrice;
      if (rowHsCode && !p.hsCode)    p.hsCode    = rowHsCode;
      if (rowCoo    && !p.coo)       p.coo       = rowCoo;

      const varSku = csvVariantSku ||
        (csvGenericSku && csvGenericSku !== p.skuBase ? csvGenericSku : '');
      if (varSku) {
        const varPrice       = findCol(row, 'variant price');
        const varCompareAt   = findCol(row, 'variant compare at price');
        const varCostPerItem = findCol(row, 'cost per item', 'cost price');
        const varBarcode     = findCol(row, 'variant barcode');
        const varGramsRaw    = findCol(row, 'variant grams');
        const varWeightRaw   = findCol(row, 'variant weight');
        const varWeightUnit  = (findCol(row, 'variant weight unit') || findCol(row, 'variant weight metric') || 'g').toLowerCase().trim();
        const varGrams       = varGramsRaw
          ? varGramsRaw
          : varWeightRaw
          ? varWeightUnit === 'kg' ? String(Math.round(parseFloat(varWeightRaw) * 1000))
          : varWeightUnit === 'lb' || varWeightUnit === 'lbs' ? String(Math.round(parseFloat(varWeightRaw) * 453.592))
          : varWeightRaw
          : '';
        const varHsCode      = findCol(row, 'variant hs code');
        const varCoo         = findCol(row, 'variant country of origin');
        const opt1Name       = findCol(row, 'option1 name', 'opt1name') || 'Size';
        const opt1Val        = findCol(row, 'option1 value', 'opt1val');
        const opt2Name       = findCol(row, 'option2 name', 'opt2name');
        const opt2Val        = findCol(row, 'option2 value', 'opt2val');

        const existingIdx = p.variants.findIndex(v => v.sku === varSku);
        if (existingIdx >= 0) {
          const ev = p.variants[existingIdx];
          if (opt2Val) p.variantType = TWO_DIM_VARIANT_TYPE;
          p.variants[existingIdx] = { ...ev,
            opt1Name: opt1Name || ev.opt1Name, opt1Val: opt1Val || ev.opt1Val,
            opt2Name: opt2Name || ev.opt2Name, opt2Val: opt2Val || ev.opt2Val,
            price: varPrice || ev.price, compareAtPrice: varCompareAt || ev.compareAtPrice,
            costPerItem: varCostPerItem || ev.costPerItem, barcode: varBarcode || ev.barcode,
            grams: parseInt(varGrams) || ev.grams, hsCode: varHsCode || ev.hsCode, coo: varCoo || ev.coo,
          };
        } else {
          const skuSuffix = varSku.split('-').pop() || '';
          // A two-dimension variant is imported only when the sheet explicitly provides an
          // Option 2 value (e.g. Shopify's "Option2 Value" column) — no size-pattern guessing.
          if (opt2Val) p.variantType = TWO_DIM_VARIANT_TYPE;
          p.variants.push({ id: uid(), sku: varSku, opt1Name: opt1Name || 'Size', opt1Val: opt1Val || skuSuffix, opt2Name: opt2Name || '', opt2Val: opt2Val || '', price: varPrice || '', compareAtPrice: varCompareAt || '', costPerItem: varCostPerItem || '', barcode: varBarcode || '', grams: parseInt(varGrams) || 200, hsCode: varHsCode || '', coo: varCoo || '' });
        }
        if (!p.basePrice && varPrice) p.basePrice = varPrice;
        if (!p.hsCode && varHsCode) p.hsCode = varHsCode;
        if (!p.coo && varCoo) p.coo = varCoo;
        if (!p.skuBase && varSku) {
          p.skuBase = varSku.replace(/-(\d{1,2}P?|XS|S\+?|SP|M|L|XL|XXL|XXXL|\d{2}(?:DD|[A-F]))$/i, '');
          if (p.skuBase) skuToKey[p.skuBase.toUpperCase()] = key;
        }
      } else {
        // No per-row variant SKU — this sheet packs one product per row instead, with sizes
        // listed in a single delimited cell (e.g. "S, M, L, XL"). Expand that into one variant
        // per size, reusing the same SKU pattern as the Variant Presets "generate" button
        // (`${skuBase}-${SIZE}`). Product-level price/HS code/COO (captured above) apply
        // identically to every size, since the sheet only supplies one value per product.
        const sizesRaw = findCol(row, 'sizes', 'size');
        if (sizesRaw && p.skuBase) {
          const sizeList = sizesRaw.split(/[,/]/).map(s => s.trim()).filter(Boolean);
          sizeList.forEach(sizeVal => {
            const sku = `${p.skuBase}-${sizeVal.replace(/\s+/g, '')}`;
            const existingIdx = p.variants.findIndex(v => v.sku === sku);
            if (existingIdx >= 0) {
              p.variants[existingIdx] = { ...p.variants[existingIdx], opt1Val: sizeVal };
            } else {
              p.variants.push({
                id: uid(), sku, opt1Name: 'Size', opt1Val: sizeVal, opt2Name: '', opt2Val: '',
                price: p.basePrice || '', compareAtPrice: '', costPerItem: '', barcode: '',
                grams: 200, hsCode: p.hsCode || '', coo: p.coo || '',
              });
            }
          });
          if (sizeList.length && p.variantType !== TWO_DIM_VARIANT_TYPE) p.variantType = 'size';
        }
      }
    }
  }

  // Apply the gathered links. Existing images are kept and appended to rather than
  // replaced, so re-importing a sheet doesn't wipe images already uploaded in the app.
  Object.entries(imageRows).forEach(([key, list]) => {
    const p = map[key];
    if (!p || !list.length) return;
    const existing = Array.isArray(p.images) ? p.images : [];
    // Match on both the stored URL and the link it was originally fetched from: once an
    // image has been re-hosted its url points at our bucket, but the sheet still carries
    // the CDN link, and only sourceUrl connects the two.
    const known = new Set();
    existing.forEach(i => {
      if (typeof i === 'string') { known.add(i); return; }
      if (i?.url) known.add(i.url);
      if (i?.sourceUrl) known.add(i.sourceUrl);
    });
    const added = [...list]
      .sort((a, b) => a.position - b.position)
      .filter(i => !known.has(i.url))
      // `remote: true` marks a link that still points at someone else's CDN; the import
      // modal downloads those and clears the flag. A link already on our own storage —
      // a sheet round-tripped out of this app — is left alone so it isn't fetched twice.
      .map(i => ({
        url: i.url,
        filename: filenameFromUrl(i.url),
        ...(isOwnStorageUrl(i.url) ? {} : { remote: true }),
      }));
    if (!added.length) return;
    p.images = [...existing, ...added];
    Object.assign(p, syncImagesFields(p.images));
  });

  return Object.entries(map).filter(([key]) => touched.has(key)).map(([, p]) => p);
}
